import type { StatusTone } from '../contracts/common';
import type {
  IncidentStatus,
  MonitorMethod,
  MonitorMode,
  MonitorSimulatedState,
  MonitorState,
} from '../contracts/monitoring';
import { NBSP } from '../money';
import { formatDuration } from '../time';

export const INCIDENT_STATUS_LABELS: Record<IncidentStatus, string> = {
  open: 'Otwarty',
  resolved: 'Zamknięty',
};

export const INCIDENT_STATUS_TONES: Record<IncidentStatus, StatusTone> = {
  open: 'danger',
  resolved: 'neutral',
};

/** Stan monitora (zgodny z `ASSET_STATUS_LABELS`: „Działa” / „Alarm”). */
export const MONITOR_STATE_LABELS: Record<MonitorState, string> = {
  up: 'Działa',
  down: 'Alarm',
  unknown: 'Sprawdzanie',
};

export const MONITOR_STATE_TONES: Record<MonitorState, StatusTone> = {
  up: 'success',
  down: 'danger',
  unknown: 'neutral',
};

export const MONITOR_MODE_LABELS: Record<MonitorMode, string> = {
  live: 'Prawdziwe sprawdzanie',
  simulated: 'Symulacja',
};

/** „co 2 min” (Figma 12: „4 strony · co 2 min”). */
export function monitorIntervalLabel(intervalSec: number): string {
  if (intervalSec % 3600 === 0) return `co ${intervalSec / 3600} h`;
  if (intervalSec % 60 === 0) return `co ${intervalSec / 60} min`;
  return `co ${intervalSec} s`;
}

export const MONITOR_METHOD_LABELS: Record<MonitorMethod, string> = {
  GET: 'GET (cała strona)',
  HEAD: 'HEAD (tylko nagłówki)',
};

/** Przełącznik symulacji (owner/admin): stan, który zwraca symulacja. */
export const MONITOR_SIMULATED_STATE_LABELS: Record<MonitorSimulatedState, string> = {
  up: 'Działa',
  down: 'Nie działa',
};

export const MONITOR_MODE_DESCRIPTIONS: Record<MonitorMode, string> = {
  live: 'Worker co interwał odpytuje stronę (tylko publiczne adresy, porty 80 i 443).',
  simulated: 'Nic nie jest wysyłane do strony — wynik ustawia przełącznik „Symuluj awarię”.',
};

function pluralForm(n: number): 0 | 1 | 2 {
  const abs = Math.abs(n);
  if (abs === 1) return 0;
  const last = abs % 10;
  const lastTwo = abs % 100;
  return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? 1 : 2;
}

/** „1 nieudane sprawdzenie”, „2 nieudane sprawdzenia”, „5 nieudanych sprawdzeń”. */
export function failedChecksText(n: number): string {
  const forms = ['nieudane sprawdzenie', 'nieudane sprawdzenia', 'nieudanych sprawdzeń'] as const;
  return `${n}${NBSP}${forms[pluralForm(n)]}`;
}

/** „1 sprawdzenie”, „3 sprawdzenia”, „12 sprawdzeń”. */
export function checksCountText(n: number): string {
  const forms = ['sprawdzenie', 'sprawdzenia', 'sprawdzeń'] as const;
  return `${n}${NBSP}${forms[pluralForm(n)]}`;
}

/** Próg alarmu w zdaniu: „po 1 błędzie”, „po 2 błędach z rzędu”. */
export function failThresholdText(n: number): string {
  return n === 1 ? `po 1${NBSP}błędzie` : `po ${n}${NBSP}błędach z rzędu`;
}

/** Próg poprawy w zdaniu: „po 1 udanym sprawdzeniu”, „po 2 udanych sprawdzeniach z rzędu”. */
export function recoverThresholdText(n: number): string {
  return n === 1 ? `po 1${NBSP}udanym sprawdzeniu` : `po ${n}${NBSP}udanych sprawdzeniach z rzędu`;
}

/** Czas trwania awarii: „< 1 min”, „4 min”, „2 h 5 min”. */
export function incidentDurationText(seconds: number): string {
  if (seconds < 60) return `<${NBSP}1${NBSP}min`;
  return formatDuration(seconds).replace(/ (min|h)/g, `${NBSP}$1`);
}

/** Dostępność: „99.96” → „99,96%”, „100.00” → „100%”; brak danych → „—”. */
export function uptimeText(percent: string | null): string {
  if (percent === null) return '—';
  if (/^100(\.0+)?$/.test(percent)) return '100%';
  return `${percent.replace('.', ',')}%`;
}

/** Powód klasyfikacji zgłoszenia klienta (Inbox: „Dlaczego Pilne”). */
export const CLIENT_REPORT_REASON =
  'Zgłoszenie klienta: AI oznaczyło wiadomość jako pilną, monitoring nie potwierdza awarii';
export const CLIENT_REPORT_LINKED_REASON = 'Zgłoszenie klienta dołączone do otwartego incydentu';
